import { usePlaybackCurrentTs } from '@app/core/playback/hooks/usePlayback';
import {
  ScrollableWithBackToTop,
  ScrollableWithBackToTopHandle,
} from '@app/core/ui/ScrollableWithBackToTop';
import { nlpIntegration } from '@mi-front/api-clients';
import { Box, Typography } from '@mui/material';
import { RefObject } from 'react';

import { SCROLL_TOP_THRESHOLD_PX } from '../consts';
import { isTopicActiveAt, isTopicWithinLast15Min } from '../live-topics-panel-utils';
import { LiveTopicsListSkeleton } from './LiveTopicsListSkeleton';
import { LiveTopicsLoadMore } from './LiveTopicsLoadMore';
import { LiveTopicsNewTopicsPill } from './LiveTopicsNewTopicsPill';
import { LiveTopicsPanelItem } from './LiveTopicsPanelItem';

export interface LiveTopicsPanelListViewProps {
  scroll: {
    scrollableRef: RefObject<ScrollableWithBackToTopHandle | null>;
    onScrollToTop: () => void;
  };
  list: {
    isLoading: boolean;
    filteredTopics: nlpIntegration.TopicOutDto[];
    emptyMessage: string;
    newlyArrivedTopicIds: Set<string>;
    viewedInDetailTopicIds: Set<string>;
    onTopicClick: (topicId: string) => void;
  };
  newTopics: {
    label: string;
    count: number;
  };
  pagination: {
    hasMoreOlder: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    /** Start time of the oldest fetched (unfiltered) topic — used as the load-more cursor label. */
    oldestFetchedStartTime?: string;
  };
}

/**
 * Scrollable list of live topics with skeleton/empty states and the "N temas nuevos" pill.
 */
export function LiveTopicsPanelListView({
  scroll,
  list,
  newTopics,
  pagination,
}: LiveTopicsPanelListViewProps) {
  const { scrollableRef, onScrollToTop } = scroll;
  const {
    isLoading,
    filteredTopics,
    emptyMessage,
    newlyArrivedTopicIds,
    viewedInDetailTopicIds,
    onTopicClick,
  } = list;
  const { label: newTopicsPillLabel, count: newTopicsCount } = newTopics;
  const { hasMoreOlder, isLoadingMore, onLoadMore, oldestFetchedStartTime } = pagination;
  const currentTs = usePlaybackCurrentTs();
  const showLoadMore = hasMoreOlder && Boolean(oldestFetchedStartTime);

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ScrollableWithBackToTop
        ref={scrollableRef}
        onScrollToTop={onScrollToTop}
        scrollTopThresholdPx={SCROLL_TOP_THRESHOLD_PX}
        backToTopPosition="right"
        addPaddingBottom={false}
      >
        {isLoading ? (
          <LiveTopicsListSkeleton />
        ) : (
          <>
            {filteredTopics.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              filteredTopics.map((topic) => (
                <LiveTopicsPanelItem
                  key={topic.id}
                  topic={topic}
                  onClick={() => onTopicClick(topic.id)}
                  showNewChip={
                    newlyArrivedTopicIds.has(topic.id) &&
                    !viewedInDetailTopicIds.has(topic.id) &&
                    isTopicWithinLast15Min(topic)
                  }
                  isActive={isTopicActiveAt(topic, currentTs)}
                  isNew={newlyArrivedTopicIds.has(topic.id)}
                />
              ))
            )}
            {showLoadMore && oldestFetchedStartTime && (
              <LiveTopicsLoadMore
                oldestFetchedStartTime={oldestFetchedStartTime}
                onLoadMore={onLoadMore}
                isLoadingMore={isLoadingMore}
              />
            )}
          </>
        )}
      </ScrollableWithBackToTop>
      <LiveTopicsNewTopicsPill
        label={newTopicsPillLabel}
        count={newTopicsCount}
        onClick={() => scrollableRef.current?.scrollToTop()}
      />
    </Box>
  );
}
